import { createHlcClock } from '@wiztime/core';
import { createStore, createWizDb, type MigrationOutcome, type Store, type WizDb } from '@wiztime/storage';
import { BUILD_COMMITS } from '../lib/buildInfo';
import { loadOrCreateDeviceId } from '../lib/deviceId';
import { createAppSync, type AppSync } from './sync';
import { createUndoModel, type UndoModel } from './undoModel';

/** Long-lived objects shared by the whole app, created once at start-up. */
export interface Services {
  readonly db: WizDb;
  readonly store: Store;
  readonly deviceId: string;
  /** Wall clock in epoch milliseconds. */
  readonly now: () => number;
  readonly undo: UndoModel;
  readonly sync: AppSync;
}

const SHORT_HASH_LENGTH = 7;

/** The build identifier written to the operation log: the short commit hash, or 'dev'. */
export const APP_VERSION = BUILD_COMMITS[0]?.hash.slice(0, SHORT_HASH_LENGTH) ?? 'dev';

/** Web Locks name that keeps two tabs from migrating at once (data dictionary §10). */
const MIGRATION_LOCK = 'wiztime-migrate';

export function createServices(dbName?: string, deviceId: string = loadOrCreateDeviceId()): Services {
  const now = (): number => Date.now();
  const db = createWizDb(dbName);
  const clock = createHlcClock(deviceId, now);
  const store = createStore({ db, clock, deviceId, now, appVersion: APP_VERSION });
  return { db, store, deviceId, now, undo: createUndoModel(store), sync: createAppSync(store) };
}

/**
 * Bring the stored data to this build's data format before anything writes (data dictionary §10).
 * Another tab running the same migration waits on the lock; without Web Locks the steps are still
 * safe to run twice.
 */
export async function migrateStoredData(store: Store): Promise<MigrationOutcome> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (locks === undefined) return store.migrate();
  return locks.request(MIGRATION_LOCK, () => store.migrate());
}
